import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCNPJ(cnpj: string): string {
  if (!cnpj) return '';
  const numbers = cnpj.replace(/\D/g, '');
  if (numbers.length !== 14) return cnpj;
  return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

export function formatCPF(cpf: string): string {
  if (!cpf) return '';
  const numbers = cpf.replace(/\D/g, '');
  if (numbers.length !== 11) return cpf;
  return numbers.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}

export function formatDocument(document: string, type: 'cpf' | 'cnpj'): string {
  return type === 'cnpj' ? formatCNPJ(document) : formatCPF(document);
}

export function getPeriodMultiplier(period: 'monthly' | 'quarterly' | 'semi_annual' | 'annual'): number {
  switch (period) {
    case 'quarterly': return 3;
    case 'semi_annual': return 6;
    case 'annual': return 12;
    default: return 1; // monthly
  }
}

export function getPeriodDays(period: 'monthly' | 'quarterly' | 'semi_annual' | 'annual'): number {
  switch (period) {
    case 'quarterly': return 90;
    case 'semi_annual': return 180;
    case 'annual': return 365;
    default: return 30; // monthly
  }
}

export function getPeriodLabel(period: 'monthly' | 'quarterly' | 'semi_annual' | 'annual'): string {
  switch (period) {
    case 'quarterly': return 'Trimestral';
    case 'semi_annual': return 'Semestral';
    case 'annual': return 'Anual';
    default: return 'Mensal';
  }
}

/**
 * Converte dias do cadastro (rental_periods.days) no enum legado quando coincide
 * com os períodos padrão. Períodos custom (ex.: 7, 15) retornam null — usar só rental_period_id.
 */
export function legacyRentalPeriodEnumFromDays(
  days: number
): 'monthly' | 'quarterly' | 'semi_annual' | 'annual' | null {
  if (days === 30) return 'monthly';
  if (days === 90) return 'quarterly';
  if (days === 180) return 'semi_annual';
  if (days === 365) return 'annual';
  return null;
}

/**
 * Fator canônico para total do ciclo vs. valor base mensal (30 dias).
 * Divisão exata com piso 1 (espelha GREATEST(1, days/30.0) das RPCs — commit 0b187ed2).
 * Ex.: 7 dias → 1; 90 dias → 3; 180 dias → 6.
 */
export function getPeriodValueMultiplierFromDays(days: number): number {
  return Math.max(1, days / 30);
}

/**
 * Valor do ciclo de locação: monthly_value × fator do período, arredondado a 2 casas.
 */
export function getRentalCycleValue(monthlyValue: number, days?: number | null): number {
  return Math.round(monthlyValue * getPeriodValueMultiplierFromDays(days ?? 30) * 100) / 100;
}


export function formatPhone(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/[^\d]/g, '');
  
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 2) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
  if (cleaned.length <= 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
  // Celular com 11 dígitos
  return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
}

/**
 * Converte string para Title Case (primeira letra de cada palavra maiúscula)
 * Ex: "SAO PAULO" -> "Sao Paulo", "rio branco do sul" -> "Rio Branco Do Sul"
 */
export function toTitleCase(str: string | undefined | null): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
